import { useCallback } from "react"
import { useAccountAlias } from "resources/account"
import { useUrlDownload } from "hooks/use-url-download"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { useAnalytics } from "connected-components/analytics"
import { buildArtifactUrl, buildSharedArtifactUrl } from "./artifact-content"
import type { ArtifactUrlKind } from "./artifact-content"
import { fileExtension } from "helpers/formatters/file-extension"
import { artifactMediaType, normalizeFileArtifact, type FileArtifact } from "./artifact-contract"

interface UseArtifactDownloadResult {
  /** `threadId` is null for a shared or deliverable artifact, which is addressed by `resourceId` or `urlFor` instead. */
  downloadArtifact: (threadId: string | null, artifactId: string) => void
  isDownloading: boolean
}

export function useArtifactDownload(
  artifact: FileArtifact | null,
  artifactThreadId: string | null | undefined,
  /** Present only for an artifact opened from the Artifacts list. */
  resourceId?: string | null,
  urlFor?: (kind: ArtifactUrlKind) => string,
): UseArtifactDownloadResult {
  const accountAlias = useAccountAlias()
  const analytics = useAnalytics()
  const buildUrl = useCallback(
    (threadId: string | null, artifactId: string) => {
      if (urlFor != null) return urlFor("download")
      return resourceId != null
        ? buildSharedArtifactUrl({ accountAlias, resourceId }, "download")
        : buildArtifactUrl({ accountAlias, threadId: threadId ?? "", artifactId }, "download")
    },
    [accountAlias, resourceId, urlFor],
  )
  const onSuccess = useCallback(() => {
    if (artifact == null) return
    const { filename, contentType, sizeBytes } = normalizeFileArtifact(artifact)
    const extension = fileExtension(filename)
    analytics.track(AI_TEAMMATE_EVENTS.ARTIFACT_DOWNLOADED, {
      artifactId: artifact.id,
      artifactType: artifact.type,
      mediaType: artifactMediaType(contentType),
      ...(extension !== undefined && { extension }),
      ...(sizeBytes !== undefined && { sizeBytes }),
      ...(artifactThreadId != null && { threadId: artifactThreadId }),
    })
  }, [artifact, artifactThreadId, analytics])
  const { download, isDownloading } = useUrlDownload<[string | null, string]>({
    buildUrl,
    errorMessage: "Unable to download the file. Please try again.",
    onSuccess,
  })
  return { downloadArtifact: download, isDownloading }
}
